import env from '~/libs/env'
import { localStorageService } from '~/libs/local-storage.service'

/**
 * ApplicationConfigService
 *
 * Facilitates api calls for Application Config models.
 *
 * Requires a store passed so it has access to the correct `$axios` in context
 * for any XHR requests. Helps manage the interface of saving answers between
 * local storage and the actual service.
 */
export class ApplicationConfigService {
    constructor($axios, $axiosWithRetry) {
      this.$axios = $axios
      this.$axiosWithRetry = $axiosWithRetry
    }

    async getResumeAppExperience({ borrowerId, tenantId, type= 'default' }) {
      if (!borrowerId || !tenantId) {
        return { data: null, error: new Error('borrowerId and tenantId are required to get resume app experience') }
      }

      const url = `${env('apiUrl')}/application-experience-resume/borrower/${borrowerId}/tenant/${tenantId}`

      let error = null
      const response = await this.$axios.get(url, { params: { type } })
        .catch(err => {
          error = err
        })

      const data = response?.data?.data ?? null
      return { data, error }
    }

    async postApplicationExperienceEvent({ moduleIdentifier, tenantId, eventType, entrypointIdentifier = null, borrowerId = null, anonymousId = null }) {
      if (!moduleIdentifier || !tenantId || !eventType) {
        return { data: null, error: new Error('moduleIdentifier, tenantId and eventType are required to post application experience event') }
      }

      if (!borrowerId && !anonymousId) {
        return { data: null, error: new Error('Either borrowerId or anonymousId is required to post application experience event') }
      }

      let url = `${env('apiUrl')}/borrower-portal/application-event/tenant/${tenantId}`
      url += borrowerId ? `/borrower/${borrowerId}` : `/anonymous/${anonymousId}`

      const body = {
        eventType,
        moduleIdentifier,
        entrypointIdentifier
      }

      let error = null

      const response = await this.$axiosWithRetry.post(url, body)
        .catch(err => {
          error = err
        })

      return { data: response?.data?.data ?? null, error }
    }

    loadAnswersFromStorage() {
      const borrowerAnswers = JSON.parse(localStorageService.getItem('borrowerAnswers')) || {}
      return borrowerAnswers
    }

    async mergeAnswersToStorage(data) {
      const blockedAnswersForStorage = ['contact.ssn']

      const oldBorrowerAnswers = JSON.parse(localStorageService.getItem('borrowerAnswers')) || {}
      const newBorrowerAnswers = Object.entries(data).reduce((acc, [key, value]) => {
        if (blockedAnswersForStorage.includes(key)) {
          return acc
        }

        acc[key] = {
          alias: key,
          value: value
        }
        return acc
      }, {})

      const storeBorrowerAnswers = { ... oldBorrowerAnswers, ... newBorrowerAnswers }
      const storage = JSON.stringify(storeBorrowerAnswers)

      localStorageService.setItem('borrowerAnswers', storage)
    }

    /**
     * @param {{[string]: any}} answers
     * @returns {boolean} successful
     */
    async saveAnswers({ answers, borrowerId }) {
      if (!process.client) {
        log.error('Tried to save answers during server-side render', new Error())
        return false
      }

      const { data } = await this.$axios.post(`${env('apiUrl')}/answers/borrower/${borrowerId}`, answers)
        .then(response => response.data)
        .catch(err => {
          if (err && err.response && err.response.status > 401) {
            log.error('Unable to save answers', Object.keys(answers), err)
          }
          return { data: false }
        })

      return data
    }
}

export default {
  ApplicationConfigService
}
